<template>
  <ListingsFiltersSection
    title="Contact Type"
    :error-message="!filters.contact_type.length ? 'Please select at least one contact type' : undefined"
  >
    <VCheckbox
      v-model="filters.contact_type"
      :value="EContactType.VENDOR"
      label="Show vendors"
      class="listings-filters"
    />
    <VCheckbox
      v-model="filters.contact_type"
      :value="EContactType.LANDLORD"
      label="Show landlords"
      class="listings-filters"
    />
    <VCheckbox
      v-model="filters.contact_type"
      :value="EContactType.APPLICANT"
      label="Show applicants"
      class="listings-filters"
    />
  </ListingsFiltersSection>
  <ListingsFiltersSection title="Show contacts since" class="show-contacts-since">
    <ListingsFilterControl label="Show vendors since">
      <DatePicker
        v-model="filters.vendor_since"
        placeholder="Select date"
        :disabled="!filters.contact_type.includes(EContactType.VENDOR)"
      />
    </ListingsFilterControl>
    <ListingsFilterControl label="Show landlords since">
      <DatePicker
        v-model="filters.landlord_since"
        placeholder="Select date"
        :disabled="!filters.contact_type.includes(EContactType.LANDLORD)"
      />
    </ListingsFilterControl>
    <ListingsFilterControl label="Show applicants since">
      <DatePicker
        v-model="filters.applicant_since"
        placeholder="Select date"
        :disabled="!filters.contact_type.includes(EContactType.APPLICANT)"
      />
    </ListingsFilterControl>
  </ListingsFiltersSection>
  <ListingsFiltersSection>
    <ListingsPropertyStatusDropdown
      v-model="filters.entity_status"
      :data-client="props.opportunityDataClient"
    />
  </ListingsFiltersSection>
  <ListingsFiltersSection>
    <VCheckbox
      v-if="shouldShowArchived"
      v-model="showArchived"
      :disabled="isShowArchivedDisabled"
      label="Show archived"
    />
  </ListingsFiltersSection>
</template>

<script lang="ts" setup>
  // Types
  import type { TAuthStore } from '~/modules/auth/store/types';
  import type { TListingFilters, TOpportunityDataClientModel } from '~/misc/listings/types';
  import type { AbstractDataClient } from '~/misc/services/data-client';
  import { EContactType } from '~/misc/listings/types';

  // Store
  import { useAuthStore } from '~/modules/auth/store';

  // Components
  import ListingsFiltersSection from '~/misc/listings/filters/section/ListingsFiltersSection.vue';
  import VCheckbox from '~/misc/v-checkbox';
  import ListingsPropertyStatusDropdown from '~/misc/listings/filters/data/property-status/ListingsPropertyStatusDropdown.vue';
  import ListingsFilterControl from '~/misc/listings/filters/control/ListingsFilterControl.vue';
  import DatePicker from '~/misc/date-picker/DatePicker.vue';

  const props = defineProps({
    opportunityDataClient: {
      type: Object as PropType<AbstractDataClient<TOpportunityDataClientModel> | undefined>,
      required: false,
      default: undefined,
    },
  });

  const authStore: TAuthStore = useAuthStore();

  const filters = defineModel({
    type: Object as PropType<TListingFilters>,
    required: true,
  });

  const showArchived: WritableComputedRef<boolean> = computed<boolean>({
    get(): boolean {
      if (filters.value.exclude_archived === null) {
        // null = no preference → show company default
        return !!authStore.user?.company.sync_archived;
      }

      return !filters.value.exclude_archived;
    },
    set(value: boolean): void {
      filters.value.exclude_archived = Number(!value);
    },
  });

  const shouldShowArchived: ComputedRef<boolean> = computed<boolean>(
    () => !!authStore.user?.company.sync_archived
  );

  const isShowArchivedDisabled: ComputedRef<boolean> = computed<boolean>(
    () => !filters.value.contact_type.length
  );

  watch(
    () => filters.value.contact_type,
    (newVal: EContactType[]) => {
      // Clear date filters for deselected contact types
      if (!newVal.includes(EContactType.VENDOR)) {
        filters.value.vendor_since = null;
      }

      if (!newVal.includes(EContactType.LANDLORD)) {
        filters.value.landlord_since = null;
      }

      if (!newVal.includes(EContactType.APPLICANT)) {
        filters.value.applicant_since = null;
      }

      if (newVal.length) return;

      filters.value.exclude_archived = 1;
    }
  );
</script>
